export interface FeedbackScreenshotRecoveryDeps {
  isFinalized: () => Promise<boolean>;
  markFailed: () => Promise<'ready' | 'failed'>;
  removeUpload: () => Promise<void>;
}

/**
 * Recover an ambiguous screenshot finalization without deleting an object
 * whose paired ready write may already have committed. Failure state is
 * recorded before cleanup, so a delayed ready write and deletion cannot cross.
 */
export async function recoverFeedbackScreenshotFinalize(
  deps: FeedbackScreenshotRecoveryDeps,
): Promise<'ready' | 'failed' | 'pending'> {
  if (await deps.isFinalized()) return 'ready';

  try {
    const settled = await deps.markFailed();
    if (settled === 'ready') return 'ready';
    try {
      await deps.removeUpload();
      return 'failed';
    } catch {
      // The failure record is authoritative, but keep retrying cleanup so an
      // uploaded private object is not left behind indefinitely.
      return 'pending';
    }
  } catch {
    // A lost acknowledgement may mean either the ready or failed transaction
    // won. Do not claim failure or delete until a later retry can arbitrate it.
  }

  if (await deps.isFinalized()) return 'ready';
  return 'pending';
}

/**
 * Remove every file a report's screenshot set may hold. Each path is tried,
 * even after one fails, so one stuck delete never leaves the others behind;
 * the first failure is then rethrown so recovery stays pending and a later
 * pass tries again. A file that never uploaded is the remover's to forgive
 * (Firebase's object-not-found), not this helper's.
 */
export async function removeFeedbackScreenshotSet(
  paths: readonly string[],
  remove: (path: string) => Promise<void>,
): Promise<void> {
  let firstError: unknown = null;
  let failed = false;
  for (const path of paths) {
    try {
      await remove(path);
    } catch (error) {
      if (!failed) firstError = error;
      failed = true;
    }
  }
  if (failed) throw firstError;
}
